/**
 * `sync.decks.deck(id).notes.*` — Yjs-backed notes facade (personal scope).
 *
 * After the per-note Y.Doc refactor each note lives in its own NoteDoc,
 * managed by `noteDocRegistry`. This facade is now a thin lookup layer:
 *
 *   - **Sync access** (`fragment`, `peekFragment`, `getJSON`, `getKind`,
 *     `setYoutubeMeta`, etc.) does `registry.peek({...})` and degrades
 *     gracefully (returns `null` / no-ops) when no NoteDoc is currently
 *     loaded for that note. In practice the editor mount path keeps
 *     a refcount on the default note, so accesses from inside the
 *     deck UI hit a live NoteDoc.
 *
 *   - **React hooks** (`useFragment`, `useKind`, `useIds`) go through
 *     `useNoteDoc(deckId, noteId)`, which acquires/releases via the
 *     registry and re-renders on doc updates + sync flips.
 *
 *   - **Mutations** (`setYoutubeMeta`, `setLinkMeta`, `delete`,
 *     `deleteAll`, `transact`) require a live NoteDoc. They no-op
 *     when none is loaded — the caller usually has the editor open
 *     (which keeps the doc alive) so this is a tolerable corner case.
 *     If a non-editor mutation path needs to land, it should acquire
 *     a NoteDoc explicitly via the registry.
 *
 * The Yjs sub-map keyed by deckPath was the old shape; that pattern
 * is gone (we now key by deckId in the docKey directly via
 * `noteDocKey(deckId, noteId)`). `resolveYjsDeckKey` still translates
 * a real DeckId into the deckPath the existing call sites use, so
 * the on-the-wire docKey matches the legacy storage convention.
 */

import { useEffect, useState } from 'react';
import type * as Y from 'yjs';

import { isNoteDocEffectivelyEmpty } from '../../notes/note-emptiness';
import type { JSONContent } from '../internal/editor-types';
import { getActivePersonalScope, type PersonalScope } from '../yjs/active-deck-docs';
import {
  DEFAULT_NOTE_ID,
  type LinkNoteMeta,
  type NoteKind,
  type YoutubeNoteMeta,
} from '../yjs/deck-doc';
import { noteFragmentToJSON } from '../yjs/hooks';
import type { RecallAttempt } from '../yjs/note-doc';
import { noteDocRegistry } from '../yjs/note-doc-registry';
import { useNoteDoc } from '../yjs/use-note-doc';
import { deleteDeckNotes, resolveYjsDeckKey } from './helpers';
import { type NoteReadResult, readNoteDoc } from './note-read';
import type { ScopedNotes } from './types';

const EMPTY_READ_REPEAT_SET: ReadonlySet<string> = new Set();
const EMPTY_ATTEMPTS_MAP: ReadonlyMap<string, RecallAttempt> = new Map();

/**
 * Active NoteDoc for `(deckId, noteId)` — peek-only. Returns `null`
 * when no consumer (editor / hook) currently holds a refcount.
 */
function peekNoteDoc(scope: PersonalScope | null, deckId: string, noteId: string) {
  if (!scope) return null;
  return noteDocRegistry.peek({
    party: scope.party,
    room: scope.room,
    deckId: resolveYjsDeckKey(deckId),
    noteId,
  });
}

export function createNotesApi(deckId: string): ScopedNotes {
  const fragment = (noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, noteId);
    if (!doc) return null;
    if (!doc.synced) return null;
    return doc.getOrCreateTiptapFragment();
  };

  const peekFragment = (noteId: string = DEFAULT_NOTE_ID): Y.XmlFragment | null => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, noteId);
    return doc?.peekTiptapFragment() ?? null;
  };

  const getJSON = (noteId: string = DEFAULT_NOTE_ID): JSONContent | undefined => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, noteId);
    if (!doc) return undefined;
    if (!doc.hasContent()) return undefined;
    const frag = doc.peekTiptapFragment();
    if (!frag || frag.length === 0) return undefined;
    return noteFragmentToJSON(frag);
  };

  const targetOf = (scope: PersonalScope, noteId: string) => ({
    party: scope.party,
    room: scope.room,
    deckId: resolveYjsDeckKey(deckId),
    noteId,
  });

  const readJSON = async (
    noteId: string = DEFAULT_NOTE_ID,
    timeoutMs = 4000,
  ): Promise<NoteReadResult> => {
    const scope = getActivePersonalScope();
    if (!scope) return { json: undefined, complete: false, version: 0 };
    return readNoteDoc(targetOf(scope, noteId), scope, timeoutMs);
  };

  const loadJSON = async (
    noteId: string = DEFAULT_NOTE_ID,
    timeoutMs = 4000,
  ): Promise<JSONContent | undefined> => {
    // Fast path: an editor (or other consumer) already holds the doc.
    const peeked = getJSON(noteId);
    if (peeked) return peeked;
    // Acquired explicitly when no editor is mounted — e.g. sharing a deck
    // from the dashboard, where the notes editor never rendered.
    return (await readJSON(noteId, timeoutMs)).json;
  };

  const version = (noteId: string = DEFAULT_NOTE_ID): number => {
    const scope = getActivePersonalScope();
    return scope ? noteDocRegistry.versionOf(targetOf(scope, noteId)) : 0;
  };

  const onChange = (listener: (noteId: string) => void): (() => void) =>
    noteDocRegistry.onVersionChange((target) => {
      const scope = getActivePersonalScope();
      if (!scope || target.party !== scope.party || target.room !== scope.room) return;
      if (target.deckId !== resolveYjsDeckKey(deckId)) return;
      listener(target.noteId);
    });

  /**
   * Reports `true` if the default-note NoteDoc is loaded AND has
   * content. Returns `false` (rather than "unknown") when no NoteDoc
   * is loaded — callers should not rely on this for correctness when
   * the editor is unmounted; it's a soft check intended for code paths
   * that already run alongside an active editor.
   */
  const hasAny = (): boolean => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, DEFAULT_NOTE_ID);
    return doc?.hasContent() ?? false;
  };

  /**
   * "Is this note safe to seed over?" — the question every seed path actually
   * needs, answered by the ONE shared predicate (`isNoteDocEffectivelyEmpty`)
   * that mobile's notes editor also uses.
   *
   * Prefer this over `hasAny()` before any seeding write. `hasAny()` reads the
   * single empty paragraph Tiptap writes into a fresh Y.Doc as content, so it
   * answers "not empty" for a note the user has never touched, and the seed is
   * skipped forever.
   *
   * UNKNOWN IS NOT EMPTY. With no NoteDoc loaded, or one that has not
   * finished its server sync, the note's content simply hasn't arrived — a
   * fresh browser has no IndexedDB copy — so this answers `false`. Answering
   * `true` there is what let the transcript seed overwrite a synced, edited
   * note. A write that must only land on an empty note should still decide at
   * write time, against the live editor: `writeNotesContent(…, { onlyIfEmpty })`.
   */
  const isEffectivelyEmpty = (noteId: string = DEFAULT_NOTE_ID): boolean => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, noteId);
    if (!doc || !doc.synced) return false;
    return isNoteDocEffectivelyEmpty(getJSON(noteId));
  };

  const getKind = (noteId: string): NoteKind | undefined => {
    return peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.getKind();
  };

  /**
   * The set of note ids currently *loaded* in the registry — there's
   * no authoritative server-side index for personal notes today, so
   * this returns only what the registry can see. Always includes
   * `DEFAULT_NOTE_ID` if its NoteDoc is loaded.
   *
   * For the multi-note future, an explicit `noteIndex: Y.Map` on the
   * root user doc will provide the canonical list.
   */
  const listIds = (): string[] => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, DEFAULT_NOTE_ID);
    return doc ? [DEFAULT_NOTE_ID] : [];
  };

  const getYoutubeMeta = (noteId: string): YoutubeNoteMeta | undefined => {
    return peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.getYoutubeMeta();
  };

  const setYoutubeMeta = (noteId: string, meta: YoutubeNoteMeta): void => {
    peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.setYoutubeMeta(meta);
  };

  const getLinkMeta = (noteId: string): LinkNoteMeta | undefined => {
    return peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.getLinkMeta();
  };

  const setLinkMeta = (noteId: string, meta: LinkNoteMeta): void => {
    peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.setLinkMeta(meta);
  };

  const del = (noteId: string): void => {
    const scope = getActivePersonalScope();
    if (!scope) return;
    void noteDocRegistry.eraseAndForget({
      party: scope.party,
      room: scope.room,
      deckId: resolveYjsDeckKey(deckId),
      noteId,
    });
  };

  const deleteAll = (): void => {
    deleteDeckNotes(deckId);
  };

  /**
   * Atomically run `fn` against the default note's Y.Doc. Used by
   * `seedNotesFromLegacyIfEmpty` to do a check-and-seed in a single
   * transaction. No-op if no NoteDoc is currently loaded — caller
   * is responsible for ensuring one is acquired (typically by the
   * editor being mounted) before calling.
   */
  const transact = (fn: () => void): void => {
    const doc = peekNoteDoc(getActivePersonalScope(), deckId, DEFAULT_NOTE_ID);
    if (!doc) return;
    doc.doc.transact(fn);
  };

  // ── Reactive hooks ─────────────────────────────────────────────────

  const useFragment = (noteId?: string): Y.XmlFragment | null => {
    return useNoteDoc(resolveYjsDeckKey(deckId), noteId).fragment;
  };

  const useSynced = (noteId?: string): boolean => {
    return useNoteDoc(resolveYjsDeckKey(deckId), noteId).synced;
  };

  const useError = (noteId: string = DEFAULT_NOTE_ID) => {
    return useNoteDoc(resolveYjsDeckKey(deckId), noteId).error;
  };

  const useKind = (noteId: string): NoteKind | undefined => {
    const { doc } = useNoteDoc(resolveYjsDeckKey(deckId), noteId);
    return doc?.getKind();
  };

  const useIds = (): string[] => {
    // Subscribe to the default note's loaded state — that's the only
    // signal that flips this list today (single-default-note convention).
    const { doc } = useNoteDoc(resolveYjsDeckKey(deckId), DEFAULT_NOTE_ID);
    return doc ? [DEFAULT_NOTE_ID] : [];
  };

  // ── Read & Repeat progress ──────────────────────────────────────────

  const useReadAndRepeatProgress = (
    noteId: string = DEFAULT_NOTE_ID,
  ): ReadonlySet<string> => {
    const { doc } = useNoteDoc(resolveYjsDeckKey(deckId), noteId);
    const [completed, setCompleted] =
      useState<ReadonlySet<string>>(EMPTY_READ_REPEAT_SET);

    useEffect(() => {
      if (!doc) {
        setCompleted(EMPTY_READ_REPEAT_SET);
        return;
      }
      const map = doc.readAndRepeatMap();
      const update = () => {
        setCompleted(new Set(map.keys()));
      };
      // Seed once with the current state, then subscribe to changes.
      update();
      map.observe(update);
      return () => {
        map.unobserve(update);
      };
    }, [doc]);

    return completed;
  };

  const setReadAndRepeatCompleted = (
    paragraphId: string,
    completed: boolean,
    noteId: string = DEFAULT_NOTE_ID,
  ): void => {
    peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.setParagraphCompleted(
      paragraphId,
      completed,
    );
  };

  const resetReadAndRepeat = (noteId: string = DEFAULT_NOTE_ID): void => {
    peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.clearReadAndRepeat();
  };

  const useReadAndRepeatAttempts = (
    noteId: string = DEFAULT_NOTE_ID,
  ): ReadonlyMap<string, RecallAttempt> => {
    const { doc } = useNoteDoc(resolveYjsDeckKey(deckId), noteId);
    const [attempts, setAttempts] =
      useState<ReadonlyMap<string, RecallAttempt>>(EMPTY_ATTEMPTS_MAP);

    useEffect(() => {
      if (!doc) {
        setAttempts(EMPTY_ATTEMPTS_MAP);
        return;
      }
      const map = doc.readAndRepeatAttemptsMap();
      const update = () => {
        const next = new Map<string, RecallAttempt>();
        map.forEach((value, key) => {
          next.set(key, value);
        });
        setAttempts(next);
      };
      update();
      map.observe(update);
      return () => {
        map.unobserve(update);
      };
    }, [doc]);

    return attempts;
  };

  const setReadAndRepeatAttempt = (
    paragraphId: string,
    attempt: RecallAttempt,
    noteId: string = DEFAULT_NOTE_ID,
  ): void => {
    peekNoteDoc(getActivePersonalScope(), deckId, noteId)?.setRecallAttempt(
      paragraphId,
      attempt,
    );
  };

  return {
    fragment,
    peekFragment,
    getJSON,
    loadJSON,
    readJSON,
    version,
    onChange,
    hasAny,
    isEffectivelyEmpty,
    getKind,
    listIds,
    getYoutubeMeta,
    setYoutubeMeta,
    getLinkMeta,
    setLinkMeta,
    delete: del,
    deleteAll,
    transact,
    useFragment,
    useSynced,
    useError,
    useKind,
    useIds,
    useReadAndRepeatProgress,
    setReadAndRepeatCompleted,
    resetReadAndRepeat,
    useReadAndRepeatAttempts,
    setReadAndRepeatAttempt,
  };
}
